import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import {
  buildTechlandCardMetricsRequest,
  formatTechlandYtdCardMetricsData,
  mergeTechlandYtdCardDisplayProps,
  withCatalogFilter,
  type ITechlandYtdMetricKeysForCompute,
  type TTechlandYtdCardMetricsFormatted,
} from '@home/pages/studio/home/_utils';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';
import {
  METRIC_YTD_PROFIT,
  TECHLAND_CARD_DISPLAY_TEMPLATE_DATA_KEY,
  TECHLAND_CARD_METRICS_API,
} from '@home/pages/studio/home/_constant';
import type { ICardMetricItem } from '@home/pages/studio/home/components/TechlandYtdRevenueCard/cardMetricsTypes';
import type { ITechlandYtdRevenueCardProps } from '@home/pages/studio/home/components/TechlandYtdRevenueCard/types';

/** card_metrics formatData 内置产物；自定义 formatData 在其之上二次加工 */
export type TTechlandYtdCardMetricsFormattedOutput = TTechlandYtdCardMetricsFormatted & {
  _cardProps?: ITechlandYtdRevenueCardProps;
};

/**
 * card_metrics `card_list` 中 ytd_profit 项的 `time`（如 20260418），格式化为副标题用的 YYYY-MM-DD。
 */
function formatSubtitleDateFromYtdProfitTime(res: unknown): string | undefined {
  const envelope = res as { card_list?: ICardMetricItem[]; };
  const list = envelope?.card_list;
  if (!Array.isArray(list)) {
    return undefined;
  }
  const item = list.find(i => i?.metrics_name === METRIC_YTD_PROFIT);
  const raw = item?.time;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return undefined;
  }
  const s = String(raw).trim();
  if (/^\d{8}$/.test(s)) {
    const d = moment(s, 'YYYYMMDD', true);
    return d.isValid() ? d.format('YYYY-MM-DD') : s;
  }
  const d = moment(s);
  return d.isValid() ? d.format('YYYY-MM-DD') : s;
}

export interface ITechlandYtdCardMetricsServiceProps {
  showTimeMap?: Record<string, string>;
  showTimeLoading?: boolean;
  data?: Record<string, unknown>;
  /** HomeFilter 注入，需写入 Service 的 data.filter 供 beforeRequest 使用 */
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  /** 与 pageConfig 中原 TechlandYtdRevenueCardWithData 一致，用于与接口结果合并 */
  cardDisplayTemplate?: ITechlandYtdRevenueCardProps;
  /** 透传给接口的 metrics（与后端返回 metrics_name 一致，不做转换） */
  metrics?: string[];
  /**
   * 额外请求参数；与内置 build 结果浅合并，同名键以此处为准（覆盖）。
   * `data.extraParams`（见 trackData）也可传入，优先级：组件 props.extraParams > data.extraParams。
   */
  extraParams?: Record<string, unknown>;
  /**
   * 在内置 format（YTD 计算、_cardProps 合并等）完成后调用，对接口数据做二次加工。
   * 第三参为接口原始 `data` 主体（与 Service 传入 formatData 的首参一致）。
   */
  formatData?: (
    formatted: TTechlandYtdCardMetricsFormattedOutput,
    filterVal?: unknown,
    rawApiData?: unknown,
  ) => unknown;
  children?: React.ReactNode;
  filterLoading?: boolean;
  trackData?: string[];
  style?: React.CSSProperties;
  className?: string;
  api?: string;
  /** 写入 data 的 key，默认 card_data；By Game 分游戏等场景可配置独立 key */
  dataKey?: string;
  /** catalog 筛选；写入请求 filters.custom_filters */
  catalog?: string;
  /** 透传给 Service，开关打开时在请求中追加 special deals logical_flag */
  appendSpecialDealsFlag?: boolean;
}

/**
 * Techland YTD：封装 dashboard Service；formatData 产出 _ytdComputed、_cardProps（合并模板与数值）。
 */
const TechlandYtdCardMetricsService: React.FC<ITechlandYtdCardMetricsServiceProps> = props => {
  const {
    showTimeMap,
    children,
    filterLoading: propsFilterLoading,
    trackData = ['filterInitData'],
    data: propsData,
    filter: filterFromParent,
    filterVal: filterValFromParent,
    cardDisplayTemplate,
    api = TECHLAND_CARD_METRICS_API,
    metrics,
    extraParams: extraParamsFromProps,
    formatData: formatDataFromProps,
    style,
    className,
    dataKey = 'card_data',
    catalog,
    appendSpecialDealsFlag,
  } = props || {};

  const mergedExtraParamsForTrack = useMemo(() => {
    const fromParent = (propsData as { extraParams?: Record<string, unknown>; } | undefined)
      ?.extraParams;
    return {
      ...(fromParent ?? {}),
      ...(extraParamsFromProps ?? {}),
    };
  }, [propsData, extraParamsFromProps]);

  const serviceData = useMemo(
    () => ({
      ...(propsData ?? {}),
      filter:
        filterFromParent ??
        filterValFromParent ??
        (propsData as { filter?: Record<string, unknown>; } | undefined)
          ?.filter,
      ...(cardDisplayTemplate
        ? { [TECHLAND_CARD_DISPLAY_TEMPLATE_DATA_KEY]: cardDisplayTemplate }
        : {}),
      ...(Object.keys(mergedExtraParamsForTrack).length > 0
        ? { extraParams: mergedExtraParamsForTrack }
        : {}),
    }),
    [
      propsData,
      filterFromParent,
      filterValFromParent,
      cardDisplayTemplate,
      mergedExtraParamsForTrack,
    ],
  );

  const formatDataImpl = useCallback(
    (res: any, filterVal: any) => {
      const keysForCompute: Partial<ITechlandYtdMetricKeysForCompute> = {};
      if (cardDisplayTemplate?.actualValueKey) {
        keysForCompute.revenueMetricName = cardDisplayTemplate.actualValueKey;
      }
      const budgetKey = cardDisplayTemplate?.budgetGroup?.ytd?.valueKey;
      const planKey = cardDisplayTemplate?.planGroup?.ytd?.valueKey;
      if (budgetKey) {
        keysForCompute.budgetMetricName = budgetKey;
      }
      if (planKey) {
        keysForCompute.planMetricName = planKey;
      }
      const formatted = formatTechlandYtdCardMetricsData(
        res,
        filterVal,
        showTimeMap,
        keysForCompute,
      );
      const ytdProfitSubtitle = formatSubtitleDateFromYtdProfitTime(res);
      const formattedWithProfitTime =
        ytdProfitSubtitle !== undefined
          ? {
            ...formatted,
            _ytdSubtitleDate: { subtitleDate: ytdProfitSubtitle },
          }
          : formatted;
      let out: TTechlandYtdCardMetricsFormattedOutput;
      if (!cardDisplayTemplate) {
        out = formattedWithProfitTime as TTechlandYtdCardMetricsFormattedOutput;
      } else {
        const cardData = mergeTechlandYtdCardDisplayProps(
          formattedWithProfitTime,
          filterVal,
          cardDisplayTemplate,
        );
        out = {
          ...formattedWithProfitTime,
          _cardProps: cardData,
        };
      }
      if (typeof formatDataFromProps === 'function') {
        return formatDataFromProps(out, filterVal, res);
      }
      return out;
    },
    [showTimeMap, cardDisplayTemplate, formatDataFromProps],
  );

  const beforeRequest = useCallback(
    (reqData: any) => {
      const fromData = (reqData as { extraParams?: Record<string, unknown>; } | undefined)
        ?.extraParams;
      const mergedExtra = {
        ...(fromData ?? {}),
        ...(extraParamsFromProps ?? {}),
      };
      const base = buildTechlandCardMetricsRequest(reqData, metrics);
      const filters = catalog
        ? withCatalogFilter(base.filters as Record<string, unknown>, catalog)
        : base.filters;
      const params = {
        ...base,
        ...mergedExtra,
        ...(catalog ? { filters } : {}),
      };
      return applySpecialDealsFlag(params, reqData, appendSpecialDealsFlag);
    },
    [metrics, extraParamsFromProps, catalog, appendSpecialDealsFlag],
  );

  const mergedTrackData = useMemo(() => {
    const base = Array.isArray(trackData) ? [...trackData] : ['filterInitData'];
    if (
      Object.keys(mergedExtraParamsForTrack).length > 0 &&
      !base.includes('extraParams')
    ) {
      base.push('extraParams');
    }
    return mergeSpecialDealsTrackData(base, appendSpecialDealsFlag);
  }, [trackData, mergedExtraParamsForTrack, appendSpecialDealsFlag]);

  return (
    <div className={className} style={style}>
      <Service
        api={api}
        dataKey={dataKey}
        trackData={mergedTrackData}
        filterLoading={propsFilterLoading}
        beforeRequest={beforeRequest}
        formatData={formatDataImpl}
        data={serviceData}
      >
        {children}
      </Service>
    </div>

  );
};

export default TechlandYtdCardMetricsService;
